import { AnalyticsService } from 'base/analytics';
import { LibraryCache } from 'base/library_cache';
import { MAX_CONCEPTS } from 'base/styleguide/model';
import { IconChecked, IconOption } from 'base/ui/icons';
import { Rows } from 'base/ui/layout';
import { MenuButton } from 'base/ui/menu';
import { menuItem } from 'base/ui/menu_model';
import { PanelLayout } from 'base/ui/panel';
import { TextSmall } from 'base/ui/typography';
import { BlocksService } from 'blocks/blocks_service';
import { SelectionStore } from 'blocks/editor/selection/store';
import { SelectionActions } from 'blocks/editor/selection_actions';
import { ProjectSession } from 'blocks/editor/session/session';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import React, { useState } from 'react';
import { Assistant } from '../ai/assistant';
import { EditorMode, editorModeStore } from '../editor_mode';
import { SidebarNavigation } from '../sidebar/routes';
import { PreviewImage } from '../styleguide/concept_previews';
import {
  ConceptAPI,
  getGlobalConceptsStore,
} from '../styleguide/concept_store';
import { styleguideConceptId } from '../styleguide/routes';
import { getGlobalDesignStore } from './design_store';
import styles from './style_guide_panel.css';

export const DesignStyleGuidePanel = observer(function _DesignStyleGuidePanel({
  navigation,
  assistant,
  analytics,
  session,
  selection,
  selectionActions,
  library,
  blocksService,
}: {
  navigation: SidebarNavigation;
  assistant: Assistant;
  analytics: AnalyticsService;
  session: ProjectSession;
  selection: SelectionStore;
  selectionActions: SelectionActions;
  library: LibraryCache;
  blocksService: BlocksService;
}) {
  const conceptsStore = getGlobalConceptsStore();

  return (
    <PanelLayout title="Style Guide" navigation={navigation}>
      <Rows spacing={16}>
        {conceptsStore.concepts.map((concept, index) => (
          <ConceptCard
            key={concept.state.uuid}
            concept={concept}
            index={index}
            assistant={assistant}
            analytics={analytics}
            session={session}
            selection={selection}
            selectionActions={selectionActions}
            library={library}
            blocksService={blocksService}
          />
        ))}
      </Rows>
    </PanelLayout>
  );
});

const ConceptCard = observer(function _ConceptCard({
  concept,
  index,
  assistant,
  analytics,
  session,
  selection,
  selectionActions,
  library,
  blocksService,
}: {
  concept: ConceptAPI;
  index: number;
  assistant: Assistant;
  analytics: AnalyticsService;
  session: ProjectSession;
  selection: SelectionStore;
  selectionActions: SelectionActions;
  library: LibraryCache;
  blocksService: BlocksService;
}) {
  const conceptsStore = getGlobalConceptsStore();
  const designStore = getGlobalDesignStore();

  const isSelected =
    designStore.design.linkedConcept.state.uuid === concept.state.uuid;

  const handleClick = () => {
    designStore.setLinkedConcept(concept);
    concept.ensureDesignSchemesSyncedAndApplied(assistant);
  };

  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div
      className={classNames(styles.card, isSelected && styles.selected)}
      onClick={handleClick}
    >
      <div className={styles.preview}>
        <PreviewImage
          analytics={analytics}
          session={session}
          selection={selection}
          selectionActions={selectionActions}
          library={library}
          concept={concept}
          blocksService={blocksService}
          size="small"
        />
      </div>
      <div className={styles.cardTitleRow}>
        <TextSmall>Concept {index + 1}</TextSmall>
        {isSelected && (
          <div className={styles.selectedIcon}>
            <IconChecked />
          </div>
        )}
      </div>
      <div
        className={classNames(styles.moreButton, menuOpen && styles.menuOpen)}
      >
        <MenuButton
          icon={IconOption}
          menu={[
            menuItem({
              label: 'Edit',
              onClick: () => {
                editorModeStore.set(EditorMode.Styleguide);
                styleguideConceptId.set(concept.state.uuid);
              },
            }),
            menuItem({
              label: 'Duplicate',
              onClick: () => {
                const {
                  uuid: _,
                  galleryPosition: __,
                  ...conceptState
                } = conceptsStore.concepts[index].state;
                conceptsStore.createNewConcept(conceptState);
              },
              disabled: conceptsStore.concepts.length === MAX_CONCEPTS,
              disabledReason: `Cannot create more than ${MAX_CONCEPTS} concepts`,
            }),
            menuItem({
              label: 'Delete',
              onClick: () => {
                conceptsStore.deleteConcept(concept.state.uuid);
              },
              disabled: conceptsStore.concepts.length === 1,
              disabledReason: 'Cannot delete the last concept',
            }),
          ]}
          search="hidden"
          onOpen={() => setMenuOpen(true)}
          onClose={() => setMenuOpen(false)}
        />
      </div>
    </div>
  );
});
